import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const ShareButton = ({ notebookId, notebookTitle }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isPublic, setIsPublic] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const { token } = useAuth();

    // Get API base URL from config or env
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';



    const fetchShareStatus = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/notebooks/${notebookId}/share`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setIsPublic(data.is_public);
                // Construct full URL
                const fullUrl = `${window.location.origin}${data.share_url}`;
                setShareUrl(fullUrl);
            }
        } catch (error) {
            console.error('Error fetching share status:', error);
        } finally {
            setLoading(false);
        }
    }, [notebookId, token, API_BASE_URL]);

    useEffect(() => {
        if (isOpen && notebookId) {
            fetchShareStatus();
        }
    }, [isOpen, notebookId, fetchShareStatus]);

    const toggleShare = async () => {
        // Only allow enabling share, not disabling
        if (isPublic) {
            return;
        }

        try {
            setLoading(true);
            const newStatus = true;

            const response = await fetch(`${API_BASE_URL}/api/notebooks/${notebookId}/share`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ is_public: newStatus })
            });

            if (response.ok) {
                const data = await response.json();
                setIsPublic(data.is_public);
                const fullUrl = `${window.location.origin}${data.share_url}`;
                setShareUrl(fullUrl);
            }
        } catch (error) {
            console.error('Error toggling share:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
            </button>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Share Notebook</h3>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Public Access Toggle */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900">Public Access</h4>
                                    <p className="text-xs text-gray-500">Anyone with the link can view</p>
                                </div>
                                <button
                                    onClick={toggleShare}
                                    disabled={loading || isPublic}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                        isPublic
                                            ? 'bg-blue-600 cursor-not-allowed opacity-75'
                                            : 'bg-gray-200 cursor-pointer'
                                    }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPublic ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                            </div>

                            {/* Share Link */}
                            {isPublic && (
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Share Link
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={shareUrl}
                                            className="flex-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                        <button
                                            onClick={copyToClipboard}
                                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            {copied ? (
                                                <span className="text-green-600 flex items-center gap-1">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Copied
                                                </span>
                                            ) : (
                                                'Copy'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Email Sharing (Coming Soon) */}
                            <div className="pt-4 border-t border-gray-200 opacity-60">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-gray-900">Invite by Email</h4>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                        Coming Soon
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        disabled
                                        placeholder="Enter email address"
                                        className="flex-1 block w-full rounded-md border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    <button
                                        disabled
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                                    >
                                        Invite
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShareButton;
